import { useEffect, useRef, useState } from 'react';
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCartStore } from '@/stores/cartStore';
import { formatPrice } from '@/lib/shopify';
import { cn } from '@/lib/utils';
import { isSoldOut } from '@/lib/stock';
import { toast } from 'sonner';

import { getCurrentLeadSessionId, updateConsultationLead } from '@/lib/leadIdentity';
import { dlViewCart, shopifyNodeToItem } from '@/lib/dataLayer';
import { ReferralBanner } from './ReferralBanner';
import { updateQuizLeadFunnel } from '@/lib/quizLeadFunnel';
import { FREE_SHIPPING_TEXT, SHIPPING_ETA_TEXT } from '@/config/shipping';

export const ShopifyCartDrawer = () => {
  const {
    items,
    isCartOpen,
    setIsCartOpen,
    removeItem,
    updateQuantity,
    getSubtotal,
    isLoading,
    createCheckout,
  } = useCartStore();

  const subtotal = getSubtotal();
  const [isCheckingOut, setIsCheckingOut] = useState(false);


  const hasTrackedViewCart = useRef(false);

  // Track view_cart when drawer opens with items
  useEffect(() => {
    if (isCartOpen && items.length > 0) {
      if (!hasTrackedViewCart.current) {
        hasTrackedViewCart.current = true;
        const dlItems = items.map((item, idx) => ({
          item_id: item.product?.node?.handle || 'unknown',
          item_name: item.product?.node?.title || 'Produto',
          price: parseFloat(String(item.price?.amount)) || 0,
          quantity: item.quantity || 1,
          currency: 'BRL' as const,
          index: idx,
        }));
        dlViewCart(dlItems, subtotal);
      }
    } else {
      hasTrackedViewCart.current = false;
    }
  }, [isCartOpen, items, subtotal]);

  const hasSoldOut = items.some((i) => !i.isGift && isSoldOut(i.product?.node, i.variantId));

  const handleCheckout = async () => {
    if (hasSoldOut) {
      toast.error('Há um item esgotado no carrinho. Remova-o para continuar.', { position: 'top-center' });
      return;
    }
    // Guard against double/triple clicks while checkout is being created
    if (isCheckingOut) return;
    setIsCheckingOut(true);
    // Track begin_checkout event (non-blocking) — note: cartStore.createCheckout()
    // already dispatches trackBeginCheckout to CRM, so we do not duplicate here
    try {
      updateConsultationLead(getCurrentLeadSessionId(), 'checkout_started');
      updateQuizLeadFunnel('checkout_started');
    } catch (error) {
      if (import.meta.env.DEV) console.error('[CRM Track] begin_checkout error:', error);
    }


    // Save cart snapshot to sessionStorage before redirecting to Shopify
    try {
      const cartSnapshot = items.map((item, idx) =>
        shopifyNodeToItem(
          item.product.node,
          parseFloat(String(item.price.amount)) || 0,
          item.quantity,
          'checkout',
          idx,
        )
      );
      // Save cart state before Shopify redirect — needed for purchase tracking on return
      sessionStorage.setItem('nathof_cart_snapshot', JSON.stringify(cartSnapshot));
      sessionStorage.setItem('nathof_last_order_value', String(subtotal));
    } catch (e) {
      console.warn('[Cart] Failed to save cart snapshot:', e);
    }

    // Internal analytics-ingest begin_checkout beacon (fire-and-forget, keepalive)
    try {
      const { sendBeginCheckoutBeacon } = await import('@/lib/analytics');
      sendBeginCheckoutBeacon({
        value: subtotal,
        items: items.map((item) => ({
          sku: item.product?.node?.handle,
          quantity: item.quantity || 1,
          price: parseFloat(String(item.price?.amount)) || 0,
        })),
      });
    } catch { /* non-blocking */ }

    try {
      const checkoutUrl = await createCheckout();
      if (checkoutUrl) {
        const { decorateCheckoutUrl } = await import('@/lib/analytics');
        // GA4 cross-domain: flush via beacon before navigating to Shopify checkout
        try {
          const w = window as unknown as { gtag?: (...args: unknown[]) => void };
          w.gtag?.('event', 'external_navigation', { transport_type: 'beacon', destination: 'shopify_checkout' });
        } catch { /* non-blocking */ }
        // createStorefrontCheckout already added return_url with total — use it directly
        window.location.href = await decorateCheckoutUrl(checkoutUrl);
        setIsCartOpen(false);
      } else {
        toast.error('Erro ao criar checkout. Tente novamente.');
        setIsCheckingOut(false);
      }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Checkout failed:', error);
      toast.error('Erro ao criar checkout. Tente novamente.');
      setIsCheckingOut(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          "fixed inset-0 bg-foreground/40 z-50 transition-opacity duration-300",
          isCartOpen ? "opacity-100 visible" : "opacity-0 invisible"
        )}
        onClick={() => setIsCartOpen(false)}
      />

      {/* Drawer */}
      <div
        className={cn(
          "fixed top-0 right-0 h-full w-full max-w-md bg-background z-50 shadow-xl transition-transform duration-300 ease-out flex flex-col",
          isCartOpen ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Seu Carrinho ({items.length})
          </h2>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 hover:bg-accent rounded-full transition-colors"
            aria-label="Fechar carrinho"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Frete grátis agora aparece como linha do total, não como faixa avulsa.
            Ver bloco "Totais" no rodapé. */}

        {/* Cart Items */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag className="w-16 h-16 text-muted mb-4" />
              <p className="text-muted-foreground mb-4">Seu carrinho está vazio</p>
              <Button onClick={() => setIsCartOpen(false)} asChild>
                <Link to="/loja">Explorar Produtos</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {[...items]
                .sort((a, b) => Number(a.isGift ?? false) - Number(b.isGift ?? false))
                .map((item) => {
                const imageUrl = item.product?.node?.images?.edges?.[0]?.node?.url;
                const isGift = item.isGift === true;
                const originalPrice = item.product?.node?.variants?.edges?.[0]?.node?.price?.amount
                  ?? item.product?.node?.priceRange?.minVariantPrice?.amount;
                return (
                  <div
                    key={item.variantId}
                    className={cn(
                      "flex gap-4 p-3 rounded-lg",
                      isGift ? "bg-primary/5 border border-primary/20" : "bg-card"
                    )}
                  >
                    {/* Product Image - Fixed white background for consistency with PDPs */}
                    <div className="w-20 h-20 bg-white rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden border border-border/20">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={item.product?.node?.title || 'Produto'}
                          className="w-full h-full object-cover"
                          width={80}
                          height={80}
                          loading="lazy"
                          onError={(e) => { (e.target as HTMLImageElement).src = '/products/placeholder.png'; }}
                        />
                      ) : (
                        <span className="text-xs text-muted-foreground text-center px-1">
                          {item.product.node.title}
                        </span>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      {isGift ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-2 py-0.5">
                            Brinde
                          </span>
                          <p className="text-sm font-medium text-foreground line-clamp-2 mt-1">
                            {item.product?.node?.title || 'Produto'}
                          </p>
                          <p className="text-sm mt-1 flex items-center gap-2">
                            {originalPrice && parseFloat(String(originalPrice)) > 0 && (
                              <span className="text-xs text-muted-foreground line-through">
                                {formatPrice(originalPrice, 'BRL')}
                              </span>
                            )}
                            <span className="font-semibold text-primary">Grátis</span>
                          </p>
                        </>
                      ) : (
                        <>
                          <Link
                            to={`/produto/${item.product?.node?.handle || ''}`}
                            className="text-sm font-medium text-foreground hover:text-primary line-clamp-2"
                            onClick={() => setIsCartOpen(false)}
                          >
                            {item.product?.node?.title || 'Produto'}
                          </Link>
                          {isSoldOut(item.product?.node, item.variantId) && (
                            <p className="text-xs font-semibold text-primary mt-1">Esgotado no momento — remova para finalizar</p>
                          )}
                          {item.variantTitle !== 'Default Title' && (
                            <p className="text-xs text-muted-foreground mt-1">{item.variantTitle}</p>
                          )}
                          <p className="text-sm font-semibold text-foreground mt-1">
                            {formatPrice(item.price.amount, item.price.currencyCode)}
                          </p>

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-2 mt-2">
                            <div className="flex items-center border border-border rounded-md">
                              <button
                                onClick={() => updateQuantity(item.variantId, item.quantity - 1)}
                                className="p-1 hover:bg-accent transition-colors"
                                aria-label="Diminuir quantidade"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="px-3 text-sm font-medium">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.variantId, item.quantity + 1)}
                                className="p-1 hover:bg-accent transition-colors"
                                aria-label="Aumentar quantidade"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <button
                              onClick={() => removeItem(item.variantId)}
                              className="p-1 text-muted-foreground hover:text-destructive transition-colors"
                              aria-label="Remover item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="shrink-0 border-t border-border p-4 space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {/* Totais — frete explícito como linha de R$ 0,00.
                Custo-surpresa no checkout é a causa nº1 de abandono; o cliente
                precisa ver o total fechado ANTES de trocar de domínio. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span className="font-medium tabular-nums">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Frete para todo o Brasil</span>
                <span className="font-semibold text-brand-moss">{FREE_SHIPPING_TEXT}</span>
              </div>
              <p className="text-xs leading-snug text-muted-foreground">{SHIPPING_ETA_TEXT}</p>
              <div className="flex items-center justify-between border-t border-border pt-2">
                <span className="font-semibold">Total</span>
                <span className="text-xl font-bold tabular-nums">{formatPrice(subtotal)}</span>
              </div>
            </div>

            <Link to="/garantia" className="block text-center text-xs text-primary underline underline-offset-2">
              Garantia 7 dias · devolvemos 100%
            </Link>

            {/* Checkout Button */}
            <Button 
              className="w-full min-h-12" 
              size="lg" 
              onClick={handleCheckout}
              disabled={isLoading || isCheckingOut || hasSoldOut}
            >
              {isLoading || isCheckingOut ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Criando Checkout...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Finalizar Compra
                </>
              )}
            </Button>

            {/* Handoff de domínio — o clique leva de nathof.com.br para
                checkout.nathof.com.br com layout Shopify. Sem aviso, a troca
                lê como redirecionamento suspeito. */}
            <p className="text-center text-xs text-muted-foreground leading-snug">
              Pagamento no ambiente seguro da Shopify · PIX, cartão ou boleto
            </p>

            {/* Indicação permanece após o checkout. */}
            <div className="space-y-3 border-t border-border pt-3">
              <ReferralBanner />
            </div>

            {/* Continue Shopping */}
            <button
              onClick={() => setIsCartOpen(false)}
              className="w-full text-center text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              Continuar Comprando
            </button>

          </div>
        )}
      </div>
    </>
  );
};
